import {
  Button,
  Modal,
  ModalClose,
  ModalContent,
  ModalFooter,
  ModalRoot,
  ModalRootProps,
} from '@utilitywarehouse/hearth-react';

import { type BroadbandAppointment } from '@generated/gql/graphql';
import { useSyncState } from '@hooks/useSyncState';
import { useTrackingContext } from '@tracking/Provider/Context';
import {
  signupApplicationEntity,
  useTrackSignupChangeBroadbandAppointmentDateSelected,
} from '@tracking/trackingEvents';

import { AppointmentStateKeys } from '../constants';
import { type SelectedAppointment } from '../types';
import { isBroadbandAppointment } from '../utils';
import { ChooseAppointmentSlot } from './ChooseAppointmentSlot';

interface ChangeAppointmentModalProps extends Pick<ModalRootProps, 'open'> {
  availableSlots: BroadbandAppointment[];
  canConfirm: boolean;
  loading?: boolean;
  onClose: () => void;
  onSelectAppointment: (appointment: SelectedAppointment) => Promise<void>;
  selectedAppointment: BroadbandAppointment;
}

export const ChangeAppointmentModal = ({
  availableSlots,
  canConfirm,
  loading = false,
  open,
  onClose,
  onSelectAppointment,
}: ChangeAppointmentModalProps) => {
  const { applicationTrackingContext } = useTrackingContext();
  const trackAppointmentSlotSelected =
    useTrackSignupChangeBroadbandAppointmentDateSelected();
  const [selectedAppointment, setSelectedAppointment] =
    useSyncState<SelectedAppointment>(
      AppointmentStateKeys.BroadbandModalTentativeAppointment,
      {
        date: null,
        slot: null,
      }
    );

  const handleSelectAppointment = (appointment: SelectedAppointment) => {
    if (!appointment.date || !appointment.slot) {
      return;
    }

    const contexts = applicationTrackingContext
      ? [signupApplicationEntity(applicationTrackingContext)]
      : [];

    trackAppointmentSlotSelected(
      {
        appointment_date: new Date(appointment.date).toISOString(),
        appointment_slot: appointment.slot,
        appointment_slot_was_suggested: appointment.suggested || false,
      },
      { contexts }
    );

    setSelectedAppointment(appointment);
  };

  const handleConfirmAppointment = async () => {
    await onSelectAppointment(selectedAppointment);
    handleOpenChange(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (loading) {
      // don't close the modal if the modal is in loading state (reserving appointment)
      return;
    }

    setSelectedAppointment({
      date: null,
      slot: null,
    });

    if (!open) {
      onClose();
    }
  };

  return (
    <ModalRoot open={open} onOpenChange={handleOpenChange}>
      <Modal
        heading="Change appointment"
        description="Need a different time? Request another engineer slot here."
      >
        <ModalContent>
          <ChooseAppointmentSlot
            disabled={loading}
            onSelectAppointment={handleSelectAppointment}
            selectedAppointment={selectedAppointment}
            availableSlots={availableSlots}
          />
        </ModalContent>
        <ModalFooter>
          <ModalClose>
            <Button colorScheme="functional" variant="ghost" disabled={loading}>
              Cancel
            </Button>
          </ModalClose>
          <Button
            colorScheme="highlight"
            variant="solid"
            loading={loading}
            disabled={
              loading ||
              !canConfirm ||
              !isBroadbandAppointment(selectedAppointment)
            }
            onClick={handleConfirmAppointment}
          >
            Select appointment
          </Button>
        </ModalFooter>
      </Modal>
    </ModalRoot>
  );
};
